import type { SearchCombinedUnitMetadata } from '@atlassian/search-client/config';

import type { PrimaryProductKey, ProductKeys } from '../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { QueryParams } from '../../../common/utils/query-params/types';
import {
	buildSelectedSites,
	getAvailableProductsForSelectedCloudIds,
} from '../../../common/utils/sites';
import {
	getSelectedFilters,
	type SelectedFiltersReturnType,
} from '../../../controllers/filters/selected/get-selected-filters';
import { getSearchConfig } from '../../../ui/search-page-relay/utils/get-search-config';

export type SearchFiltersConfig = SelectedFiltersReturnType & {
	availableProducts: ProductKeys[];
	selectedProducts: ProductKeys[];
	sites: string[];
};

export type GetSearchFiltersConfigArgs = {
	/** Raw URL query parameters */
	queryParams: QueryParams;
	/** The cloud ID of the current site */
	cloudId: string;
	/** Available product keys for the current search experience */
	availableProducts: ProductKeys[];
	/** Per-site product availability from the combined search config. */
	unitMetadata?: SearchCombinedUnitMetadata[];
	/** Products excluded from the "all" product filter */
	allFilterExcludedProducts: ProductKeys[];
	/** Third-party connector configurations from bootstrap */
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	/** The current user's account ID, used for "assigned to me" / "watched by me" filters */
	currentUserId?: string | null;
	/** JSM incident filter type IDs */
	jsmIncidentFilterIds: string[];
	/** The tenant's primary product; gates the multi-site `sites` selection. */
	primaryProduct?: PrimaryProductKey;
};

export type GetSearchFiltersConfig = (args: GetSearchFiltersConfigArgs) => SearchFiltersConfig;

/**
 * Pure function that resolves search filter configuration from URL query parameters
 * and bootstrap context.
 *
 * Composes two existing pure functions:
 * 1. getSearchConfig - converts URL query params into store-shaped filters
 * 2. getSelectedFilters - converts store-shaped filters into query variable format
 *
 * This function has no React dependencies and can be called from SSR or preloading contexts.
 */
export const getSearchFiltersConfig: GetSearchFiltersConfig = ({
	queryParams,
	cloudId,
	availableProducts,
	unitMetadata,
	allFilterExcludedProducts,
	thirdPartyConfigs,
	currentUserId,
	jsmIncidentFilterIds,
	primaryProduct,
}) => {
	const { filters, selectedProducts, selectedDatasource, sites } = getSearchConfig({
		cloudId,
		queryParams,
		thirdPartyConfigs,
		primaryProduct,
	});

	// Resolve products from the same parsed, gated sites used by the network request.
	const productsForQuery =
		unitMetadata?.length && sites.length
			? getAvailableProductsForSelectedCloudIds({
					availableProducts,
					selectedCloudIds: Array.from(buildSelectedSites(sites, unitMetadata, cloudId).keys()),
					unitMetadata,
				})
			: availableProducts;

	const selectedFilters = getSelectedFilters({
		filters,
		selectedProducts,
		selectedDatasource,
		availableProducts: productsForQuery,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		currentUserId,
		jsmIncidentFilterIds,
	});

	return {
		availableProducts: productsForQuery,
		selectedProducts,
		sites,
		...selectedFilters,
	};
};
